// src/components/AudionAnalysisPanel/index.tsx
import React, { useState, useEffect, useRef } from "react";
import * as P from "./RealtimeSttPanel.styles";
import { PanelContainer } from "../ConversationPanel/ConversationPanel.styles";
import {
  ActionType,
  CommonActionBar,
  DEFAULT_ACTION_STYLES,
} from "../../components/ActionBar";
import { useRealtimeSttStore } from "store/realtimeSttStore";
import { handleSubmit } from "@/apis/project/pipeline";
import { useNavigate } from "react-router-dom";
import { CTABanner } from "../EmotionAnalysisPanel/EmotionAnalysisPanel.styles";

const RealtimeSttPanel: React.FC = () => {
  const { finalTranscripts, currentTranscript, summary, topic, isRecording } = useRealtimeSttStore();
  const { filePath, audioFile } = useRealtimeSttStore();
  const navigate = useNavigate();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const transcriptsListRef = useRef<HTMLDivElement>(null);
  const [showOriginal, setShowOriginal] = useState(true);
  const refinedTranscripts = finalTranscripts.filter((item) => item.isRefined);
  const isSummarizing =
    !isRecording &&
    finalTranscripts.length > 0 &&
    refinedTranscripts.length === 0 &&
    !summary &&
    !topic;

  const handleCopy = () => {
    if (finalTranscripts.length === 0) {
      alert("복사할 텍스트가 없습니다.");
      return;
    }
    const textToCopy = finalTranscripts.map(item => item.text).join("\n");
    navigator.clipboard.writeText(textToCopy).then(() => {
      alert("텍스트가 복사되었습니다.");
    });
  };

  const handleDownload = () => {
    if (finalTranscripts.length === 0) {
      alert("아직 텍스트가 생성되지 않았습니다.");
      return;
    }

    const textContent = finalTranscripts.map(item => item.text).join("\n");
    const blob = new Blob([textContent], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "realtime_stt.txt";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleRealtimeSttClick = async () => {
    if (isAnalyzing) {
      alert("분석이 이미 진행 중입니다.");
      return;
    }
    if (!audioFile) {
      alert("먼저 실시간 녹음을 진행해주세요.");
      return;
    }

    setIsAnalyzing(true);
    try {
      alert("분석을 시작했습니다. 분석이 완료되면 결과페이지로 이동합니다.");
      const projectId = "audion_vu";

      const result = await handleSubmit({
        file: audioFile,
        projectId: projectId,
        setProgress: () => {}, // UI 없으므로 빈 함수
        signal: new AbortController().signal,
      });

      if (!result) throw new Error("음성이해 분석 실패");

      navigate(
        `/results/audion_vu/${projectId}/${result.documentId}`,
        {
          state: { result },
        }
      );
    } catch (error) {
      alert("분석을 시작하는 데 실패했습니다.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleRealtimeWavDownload = () => {
    if (filePath) {
      const downloadUrl = `${import.meta.env.VITE_DOWNLOAD_URL}/${filePath}`;
      const link = document.createElement("a");
      link.href = downloadUrl;
      const filename = `realtime-audio-${new Date().toISOString()}.wav`;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      alert("다운로드할 파일이 없습니다. 먼저 녹음을 완료해주세요.");
    }
  };

  const leftActions = [
    {
      type: "copy" as ActionType,
      label: "복사",
      onClick: handleCopy,
      ...DEFAULT_ACTION_STYLES.copy,
    },
    // {
    //   type: "share" as ActionType,
    //   label: "공유",
    //   onClick: onShare,
    //   ...DEFAULT_ACTION_STYLES.share,
    // },
    // {
    //   type: "edit" as ActionType,
    //   label: "수정",
    //   onClick: onEdit,
    //   ...DEFAULT_ACTION_STYLES.edit,
    // },
  ];

  const rightActions = [
    {
      type: "download" as ActionType,
      label: "다운로드",
      onClick: handleDownload,
      ...DEFAULT_ACTION_STYLES.download,
    },
  ];

  // 새 발화가 추가되거나 currentTranscript가 업데이트될 때 스크롤을 맨 아래로 이동
  useEffect(() => {
    if (transcriptsListRef.current) {
      // 약간의 지연을 두어 DOM 업데이트 후 스크롤 적용
      setTimeout(() => {
        if (transcriptsListRef.current) {
          transcriptsListRef.current.scrollTop = transcriptsListRef.current.scrollHeight;
        }
      }, 0);
    }
  }, [finalTranscripts.length, currentTranscript]);

  const hasTranscript =
    finalTranscripts.length > 0 || (currentTranscript && currentTranscript.trim() !== "");

  return (
    <PanelContainer>
      <P.RealtimeContentContainer>
        {/* 회의 요약 카드 */}
        {(summary || topic) && (
          <P.SummaryCard>
            <P.SummaryHeader>요약</P.SummaryHeader>
            {topic && (
              <P.TopicContainer>
                <P.TopicLabel>회의 주제</P.TopicLabel>
                <P.TopicText>{topic}</P.TopicText>
              </P.TopicContainer>
            )}
            {summary && (
              <P.SummaryContainer>
                <P.SummaryLabel>핵심 내용</P.SummaryLabel>
                <P.SummaryText>{summary}</P.SummaryText>
              </P.SummaryContainer>
            )}
          </P.SummaryCard>
        )}

        <P.TranscriptContainer>
          {!hasTranscript ? (
            <P.DefaultText>
              말씀하시는 내용을 실시간으로 기록해 드릴게요!
            </P.DefaultText>
          ) : (
            <P.TranscriptsList ref={transcriptsListRef}>
              <P.MemoOriginalLayout>
                {/* 좌측: 정리된 회의 메모 카드 */}
                <P.MemoColumn>
                <P.SectionHeader>회의 메모</P.SectionHeader>
                  <P.MeetingMemoWrapper>
                    {refinedTranscripts.length === 0 ? (
                      isSummarizing ? (
                        <P.MemoLoadingState>
                          ⏳ 회의 메모를 정리하고 있어요...
                        </P.MemoLoadingState>
                      ) : (
                        <P.MemoEmptyState>
                          📝 회의 메모를 자동으로 정리하고 있어요
                          <br />
                          발화가 조금 더 쌓이면 요약이 생성됩니다
                        </P.MemoEmptyState>
                      )
                    ) : (
                      <P.MeetingMemoList>
                        {refinedTranscripts.map((item, index) => (
                          <P.MeetingMemoItem key={index}>
                            <P.MemoTag>메모</P.MemoTag>
                            <span>{item.text}</span>
                          </P.MeetingMemoItem>
                        ))}
                      </P.MeetingMemoList>
                    )}
                  </P.MeetingMemoWrapper>
                </P.MemoColumn>

                {/* 우측: 원문 로그 영역 (토글 가능) */}
                {showOriginal && (
                  <P.OriginalColumn>
                    <P.OriginalHeaderRow>
                      <P.OriginalTitle>원문</P.OriginalTitle>
                      <P.OriginalToggle
                        type="button"
                        onClick={() => setShowOriginal(false)}
                      >
                        닫기
                      </P.OriginalToggle>
                    </P.OriginalHeaderRow>
                    <P.OriginalSection>
                      {finalTranscripts.map((item, index) => (
                        <P.OriginalLogItem key={index}>
                          {item.originalText || item.text}
                        </P.OriginalLogItem>
                      ))}
                      {currentTranscript && (
                        <P.CurrentTranscript>{currentTranscript}</P.CurrentTranscript>
                      )}
                    </P.OriginalSection>
                  </P.OriginalColumn>
                )}
              </P.MemoOriginalLayout>

              {/* 우측 영역이 닫혀 있을 때는 하단에 토글 버튼만 노출 */}
              {!showOriginal && (
                <P.OriginalToggleRow>
                  <P.OriginalToggle
                    type="button"
                    onClick={() => setShowOriginal(true)}
                  >
                    원문 보기
                  </P.OriginalToggle>
                </P.OriginalToggleRow>
              )}
            </P.TranscriptsList>
          )}
        </P.TranscriptContainer>
      </P.RealtimeContentContainer>
      {/* 액션 버튼 */}
      <CTABanner onClick={handleRealtimeSttClick}>
        음성이해에서 더 자세하게 분석해보세요 →
      </CTABanner>
      <CommonActionBar leftActions={leftActions} rightActions={rightActions} />
    </PanelContainer>
  );
};

export default RealtimeSttPanel;
